import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AccordionBox from "components/Layouts/AccordionBox";
import { Grid, Typography } from "@mui/material";
import { serviceStatus } from "constants/serviceDetails";
import ProgressBar from "components/Elements/ProgressBar";
import { statusOfServices } from "constants/serviceDetails";
import { makeStyles } from "tss-react/mui";

import Icon from "components/Elements/Icon";
import { roundedToFixed } from "helpers/utils";
import ActionButtonStyle from "styles/application/ActionButtonStyle";
import GeneralButton from "components/Elements/GeneralButton";
import { findWindows } from "windows-iana";
import { ReportGenerationDialog } from "components/Modules/ReportSubscriptions";
import { toApiReportType } from "services/reportSubscriptions";

const AT = ActionButtonStyle();
const useStyles = makeStyles()((theme) => ({
	mainContainer: {
		display: "flex",
	},

	smallTxt: {
		fontFamily: "Roboto Condensed",
		fontWeight: "400",
		fontSize: "14px",
	},
	greenTxt: {
		fontFamily: "Roboto Condensed",
		fontWeight: "400",
		fontSize: "14px",
		color: "#24BA78",
	},
	inputText: {
		color: "#000000de",
	},
	custoomLabel: {
		display: "flex",
		alignItems: "center",
	},
	customLabelTxt: {
		display: "inline-flex",
		gap: 6,
	},
	resetButton: {
		backgroundColor: "#ED8738",
		marginTop: "-7px",
	},
	loaderContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
}));
const labelText = {
	fontFamily: "Roboto Condensed",
	fontWeight: "bold",
	fontSize: "14px",
	marginBottom: "15px",
};

function ServiceInformation({
	classes,
	detail,
	customCaptions,
	setOpenChnageStatusPopup,
	isReadOnly,
	serviceId,
}) {
	const { classes: classess, cx } = useStyles();
	const { t } = useTranslation();

	const [activeReportRequest, setActiveReportRequest] = useState(null);
	const closeReportDialog = () => setActiveReportRequest(null);

	const downloadServiceReport = () => {
		const time = findWindows(Intl.DateTimeFormat().resolvedOptions().timeZone);
		setActiveReportRequest({
			reportName: `${customCaptions?.service || "Service"} Report`,
			body: {
				reportType: toApiReportType("completedService"),
				filterContext: {
					serviceId: parseInt(serviceId, 10),
					timeZone: time?.[0]?.toString() || "UTC",
				},
				name: null,
				recipientEmail: null,
				sendEmail: false,
			},
		});
	};

	return (
		<AccordionBox title={t("title.entityInformation", { entity: customCaptions?.service })}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Grid container wrap="nowrap" alignItems="flex-start" sx={{ marginBottom: 3 }}>
						<Grid item>
							<Typography
								sx={labelText}
								className={cx(classess.labelText, classess.customLabelTxt)}
							>
								{t("label.status")}:
								<span style={{ display: "inline-flex", gap: 3 }}>
									<span>
										<Icon
											name={serviceStatus[detail?.status]}
											fontSize={"18px"}
										/>
									</span>
									<span
										className={classess.smallTxt}
										style={{ whiteSpace: "nowrap" }}
									>
										{statusOfServices(
											detail?.status,
											detail?.tasksSkipped,
											customCaptions
										)}
									</span>
								</span>
							</Typography>
						</Grid>

						<Grid item sx={{ marginLeft: 2 }}>
							{!isReadOnly &&
								// show Reset Button for "In Progress" & "Checked Out" Service Status
								(detail?.status === "I" || detail?.status === "H") && (
									<AT.GeneralButton
										className={classess.resetButton}
										onClick={() => setOpenChnageStatusPopup(true)}
									>
										{t("action.resetEntity", { entity: customCaptions?.service })}
									</AT.GeneralButton>
								)}
						</Grid>
					</Grid>
				</Grid>

				<Grid item lg={7} md={6} xs={12} sx={{ paddingRight: { lg: 2, md: 2, xs: 0 } }}>
					<Typography className={classess.labelText} sx={labelText}>
						{t("label.percentageCompleted")}:
						<span
							className={classess.greenTxt}
							style={{
								color:
									detail?.percentageOverTime <= 5
										? "#23BB79"
										: detail?.percentageOverTime > 5 &&
										  detail?.percentageOverTime <= 10
										? "#ED8738"
										: "#E31212",
							}}
						>
							{roundedToFixed(detail?.percentageComplete, 0)}%
						</span>
						<ProgressBar
							value={detail?.percentageComplete}
							height={"8px"}
							isLabelVisible={false}
							width={"100%"}
							bgColour={
								detail?.percentageOverTime <= 5
									? "#23BB79"
									: detail?.percentageOverTime > 5 &&
									  detail?.percentageOverTime <= 10
									? "#ED8738"
									: "#E31212"
							}
						/>
					</Typography>
					<Typography className={classess.labelText} sx={labelText}>
						{t("label.percentageTimeOverUnder")}:
						<span
							className={classess.greenTxt}
							style={{
								color:
									detail?.percentageOverTime <= 5
										? "#23BB79"
										: detail?.percentageOverTime > 5 &&
										  detail?.percentageOverTime <= 10
										? "#ED8738"
										: "#E31212",
							}}
						>
							{detail?.percentageOverTime
								? detail?.percentageOverTime + "%"
								: ""}
						</span>
					</Typography>
					<Typography className={classess.labelText} sx={labelText}>
						{t("label.timeMinsOverUnder")}:{" "}
						<span
							className={classess.greenTxt}
							style={{
								color:
									detail?.percentageOverTime <= 5
										? "#23BB79"
										: detail?.percentageOverTime > 5 &&
										  detail?.percentageOverTime <= 10
										? "#ED8738"
										: "#E31212",
							}}
						>
							{detail?.minutesOverTime}
						</span>
					</Typography>
					<Typography className={classess.labelText} sx={labelText}>
						{t("label.remainingMinutes")}:{" "}
						<span style={{ fontWeight: 400 }}>
							{roundedToFixed(detail?.estimatedMinutes, 1)}
						</span>
					</Typography>
				</Grid>
				
				<Grid item lg={5} md={6} xs={12} sx={{ paddingLeft: { lg: 2, md: 2, xs: 0 }, marginTop: { xs: 2, md: 0 } }}>
					<Typography className={classess.labelText} sx={labelText}>
						{t("label.serviceReport")}:{" "}
						<span style={{ fontWeight: 400 }}>
							{t("message.serviceReportSummary")}
						</span>
					</Typography>

					<GeneralButton
						style={{
							padding: "6px 22px",
							fontSize: "12.5px",
							width: "200px",
							borderRadius: "18px",
							marginTop: "8px",
						}}
						onClick={downloadServiceReport}
					>
						{t("action.downloadEntityReport", { entity: customCaptions?.service || "Service" })}
					</GeneralButton>
				</Grid>
			</Grid>
			{activeReportRequest ? (
				<ReportGenerationDialog
					open
					onClose={closeReportDialog}
					reportName={activeReportRequest.reportName}
					body={activeReportRequest.body}
				/>
			) : null}
		</AccordionBox>
	);
}

export default ServiceInformation;
